import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useTLoose } from '../../i18n/t-loose';
import { ChevronIcon, XIcon } from '../icons';
import { EventIcon } from './event-icon';
import {
  SEVERITY_COLORS,
  SEVERITY_LABEL_KEYS,
  SOURCE_BRANDING,
  formatRange,
  formatSource,
  resolveAlertArea,
  tierToLevel,
  timeAgo,
} from './alert-utils';
import type { FeedAlert } from '../../types';

interface AlertDetailModalProps {
  readonly alert: FeedAlert | null;
  readonly onClose: () => void;
}

const LANGUAGE_DISPLAY: Record<string, string> = {
  en: 'English', sv: 'Swedish', es: 'Spanish', fr: 'French', de: 'German',
  pt: 'Portuguese', it: 'Italian', nl: 'Dutch', pl: 'Polish', ar: 'Arabic',
  fa: 'Persian', tr: 'Turkish', hi: 'Hindi', bn: 'Bengali', ur: 'Urdu',
  zh: 'Chinese', ja: 'Japanese', ko: 'Korean', ru: 'Russian',
  uk: 'Ukrainian', th: 'Thai',
};

function languageDisplay(code: string): string {
  return LANGUAGE_DISPLAY[code] ?? code.toUpperCase();
}

export function AlertDetailModal({ alert, onClose }: AlertDetailModalProps) {
  const { t } = useTranslation();
  const tLoose = useTLoose();

  // Close on Escape — same affordance as the auth modal.
  useEffect(() => {
    if (!alert) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [alert, onClose]);

  if (!alert) return null;

  const level = tierToLevel(alert.severityTier);
  const palette = SEVERITY_COLORS[level];
  const severityLabel = t(SEVERITY_LABEL_KEYS[level]);
  const sourceLabel = formatSource(alert.sourceApi);
  const branding = SOURCE_BRANDING[alert.sourceApi];
  const area = resolveAlertArea(alert);
  const range = formatRange(alert.validFrom, alert.expiresAt);
  const issued = timeAgo(alert.issuedAt);

  // Filter out any blank descriptions so we don't render empty headings.
  const sections = (alert.descriptions ?? []).filter(
    (s) => (s.title && s.title.trim().length > 0) || (s.text && s.text.trim().length > 0),
  );

  return (
    <div className="nw-modal-bg" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="nw-alert-modal"
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Banner: full-width banner image when the source has one,
            otherwise a colour-only band tinted by source/severity. */}
        <div
          className="nw-alert-modal__banner"
          style={{
            background: branding?.bannerBg ?? palette.bg,
            backgroundImage: branding?.bannerUrl ? `url(${branding.bannerUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Render the wordmark on top of the banner colour when no
              banner image is set, so a sourceless alert still gets a
              brand cue. When the banner image is set, the wordmark is
              already baked into the image. */}
          {!branding?.bannerUrl && branding?.wordmarkUrl && (
            <img
              src={branding.wordmarkUrl}
              alt=""
              className={`nw-alert-modal__wordmark-img${branding.tintWhite ? ' is-tinted' : ''}`}
            />
          )}
          <span
            className="nw-alert-modal__severity"
            style={{
              background: palette.bg,
              borderColor: palette.border,
              color: palette.core,
            }}
          >
            <span
              className="nw-alert-modal__severity-dot"
              style={{ background: palette.core }}
              aria-hidden
            />
            {severityLabel}
          </span>
          <button
            type="button"
            className="nw-alert-modal__close"
            onClick={onClose}
            aria-label={t('web.alertsModal.a11y.close')}
          >
            <XIcon size={14} stroke={2.2} />
          </button>
        </div>

        <div className="nw-alert-modal__body">
          <div className="nw-alert-modal__source-row">
            <span className="nw-alert-modal__event-icon" style={{ color: palette.core }}>
              <EventIcon eventType={alert.eventType} size={18} color={palette.core} />
            </span>
            <span className="nw-alert-modal__source">{sourceLabel}</span>
            {alert.eventType && (
              <>
                <span className="nw-alert-modal__source-dot" />
                <span className="nw-alert-modal__event-type">{alert.eventType}</span>
              </>
            )}
          </div>

          <h2 className="nw-alert-modal__title">{alert.title}</h2>

          <dl className="nw-alert-modal__facts">
            {area && (
              <div className="nw-alert-modal__fact">
                <dt>{t('web.alertsModal.facts.where')}</dt>
                <dd>{area}</dd>
              </div>
            )}
            {range && (
              <div className="nw-alert-modal__fact">
                <dt>{t('web.alertsModal.facts.when')}</dt>
                <dd>{range}</dd>
              </div>
            )}
            <div className="nw-alert-modal__fact">
              <dt>{t('web.alertsModal.facts.issued')}</dt>
              <dd>{issued}</dd>
            </div>
            <div className="nw-alert-modal__fact">
              <dt>{t('web.alertsModal.facts.language')}</dt>
              <dd>{languageDisplay(alert.language)}</dd>
            </div>
          </dl>

          {alert.summary && alert.summary.trim().length > 0 && (
            <>
              <span className="nw-alert-modal__ai-badge">{t('common.aiSummaryBadge')}</span>
              <p className="nw-alert-modal__summary">{alert.summary}</p>
            </>
          )}

          {sections.length > 0 ? (
            <div className="nw-alert-modal__sections">
              {sections.map((s, i) => {
                // Post-PR-3 (2026-05-12): section heading resolved purely via
                // i18n on `titleCode`. `BODY` sentinel = adapters lacking
                // structure (Polisen / Krisinformation) — render text only.
                const heading =
                  s.titleCode && s.titleCode !== 'BODY'
                    ? tLoose(`alerts.descriptionSection.${s.titleCode}`)
                    : null;
                return (
                  <div key={i} className="nw-alert-modal__section">
                    {heading && <h3>{heading}</h3>}
                    {s.text &&
                      s.text.split(/\n{2,}/).map((para, j) => <p key={j}>{para}</p>)}
                  </div>
                );
              })}
            </div>
          ) : alert.body ? (
            <div className="nw-alert-modal__sections">
              {alert.body.split(/\n{2,}/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          ) : null}

          {alert.sourceUrl && (
            <a
              className="nw-btn nw-btn--primary"
              href={alert.sourceUrl}
              target="_blank"
              rel="noreferrer"
              style={{ marginTop: 16 }}
            >
              {t('web.alertsModal.readOn', { source: sourceLabel })}
              <ChevronIcon dir="right" size={14} stroke={2.2} />
            </a>
          )}

          <p className="nw-alert-modal__disclaimer">{t('alerts.detail.verifyOfficial')}</p>
        </div>
      </div>
    </div>
  );
}
